import "./FilterPanel.css";
import {
  Typography,
  TextField,
  MenuItem,
  Slider,
  Button,
  Tooltip,
  IconButton,
  Stack,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { useMemo } from "react";

export type Filters = {
  is_official: ("All" | boolean)[];
  last_updated: { from: string | null; to: string | null };
  versions: [number, number];
  category: string[];
};

type Props = {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  versionsMax: number;
};

function toOfficialArray(
  val: "All" | "Official" | "Community"
): ("All" | boolean)[] {
  if (val === "All") return [];
  if (val === "Official") return [true];
  return [false];
}

function toOfficialDisplay(
  valArr: ("All" | boolean)[] | undefined
): "All" | "Official" | "Community" {
  const v = Array.isArray(valArr) && valArr.length ? valArr[0] : "All";
  if (v === true) return "Official";
  if (v === false) return "Community";
  return "All";
}

function toCategoryArray(val: string): string[] {
  return val === "All" ? [] : [val];
}

function toCategoryDisplay(valArr: string[] | undefined): string {
  return Array.isArray(valArr) && valArr.length ? valArr[0] : "All";
}

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(n, hi));
}

const FilterPanel = ({ filters, setFilters, versionsMax }: Props) => {
  const officialOptions = ["All", "Official", "Community"] as const;

  const categoryOptions = [
    "All",
    "Integration & delivery",
    "Databases & storage",
    "Monitoring & observability",
    "Developer tools",
    "Networking",
    "Internet of things",
    "Security",
    "Web servers",
    "Content management system",
    "Web analytics",
    "Machine learning & AI",
    "Data science",
    "API management",
    "Message queues",
    "Operating systems",
    "Languages & frameworks",
  ] as const;

  const vMax = useMemo(
    () => Math.max(0, Number.isFinite(versionsMax) ? versionsMax : 0),
    [versionsMax]
  );

  const safeRange: [number, number] = useMemo(() => {
    const v = (filters as any)?.versions;
    if (
      Array.isArray(v) &&
      v.length === 2 &&
      v.every((n) => Number.isFinite(n))
    ) {
      return [
        clamp(Math.round(v[0]), 0, vMax),
        clamp(Math.round(v[1]), 0, vMax),
      ];
    }
    return [0, vMax];
  }, [filters, vMax]);

  const handleSlider = (_: Event, v: number | number[]) => {
    const [lo, hi] = (Array.isArray(v) ? v : [0, v]) as [number, number];
    const L = clamp(lo, 0, vMax);
    const H = clamp(hi, 0, vMax);
    setFilters((prev) => ({ ...prev, versions: [L, H] }));
  };

  const handleOfficialChange = (
    displayVal: "All" | "Official" | "Community"
  ) => {
    setFilters((prev) => ({
      ...prev,
      is_official: toOfficialArray(displayVal),
    }));
  };

  const handleCategoryChange = (val: string) => {
    setFilters((prev) => ({ ...prev, category: toCategoryArray(val) }));
  };

  const handleFromDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const from = e.target.value || null;
    setFilters((prev) => ({
      ...prev,
      last_updated: { ...prev.last_updated, from },
    }));
  };

  const handleToDate = (e: React.ChangeEvent<HTMLInputElement>) => {
    const to = e.target.value || null;
    setFilters((prev) => ({
      ...prev,
      last_updated: { ...prev.last_updated, to },
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      is_official: [],
      last_updated: { from: null, to: null },
      versions: [0, vMax],
      category: [],
    });
  };

  return (
    <div className="filter-panel">
      <div className="filter-section">
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          className="filter-section-title-row"
        >
          <Typography variant="subtitle2" className="filter-section-title">
            Maintainer Type
          </Typography>
          <Tooltip
            arrow
            placement="right"
            title={
              <div>
                Choose <b>Official</b> (vendor/Docker Official Images) or{" "}
                <b>Community</b> (third-party).
              </div>
            }
          >
            <IconButton
              size="small"
              className="filter-help-btn"
              aria-label="Maintainer type help"
            >
              <InfoOutlinedIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>

        <TextField
          select
          size="small"
          fullWidth
          value={toOfficialDisplay(filters?.is_official)}
          onChange={(e) =>
            handleOfficialChange(
              e.target.value as "All" | "Official" | "Community"
            )
          }
        >
          {officialOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </TextField>
      </div>

      <div className="filter-section">
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          className="filter-section-title-row"
        >
          <Typography variant="subtitle2" className="filter-section-title">
            Versions
          </Typography>
          <Tooltip
            arrow
            placement="right"
            title="Filter by number of versions/tags in a repository."
          >
            <IconButton
              size="small"
              className="filter-help-btn"
              aria-label="Versions help"
            >
              <InfoOutlinedIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>

        <div style={{ marginTop: 8 }}>
          <Slider
            value={safeRange}
            onChange={handleSlider}
            min={0}
            max={vMax || 0}
            step={1}
            valueLabelDisplay="auto"
            getAriaLabel={() => "Versions range"}
            valueLabelFormat={(v) => `${v}`}
            disableSwap
          />
          <Typography variant="caption" color="text.secondary">
            Active range: {safeRange[0]} – {safeRange[1]} (max {vMax})
          </Typography>
        </div>
      </div>

      <div className="filter-section">
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          className="filter-section-title-row"
        >
          <Typography variant="subtitle2" className="filter-section-title">
            Last Updated
          </Typography>
          <Tooltip
            arrow
            placement="right"
            title="Filter repositories by most recent update (UTC)."
          >
            <IconButton
              size="small"
              className="filter-help-btn"
              aria-label="Last updated help"
            >
              <InfoOutlinedIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={1}>
          <TextField
            label="From"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            fullWidth
            value={filters.last_updated.from ?? ""}
            onChange={handleFromDate}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            fullWidth
            value={filters.last_updated.to ?? ""}
            onChange={handleToDate}
          />
        </Stack>
      </div>

      <div className="filter-section">
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          className="filter-section-title-row"
        >
          <Typography variant="subtitle2" className="filter-section-title">
            Categories
          </Typography>
          <Tooltip
            arrow
            placement="right"
            title="Filter by the repository’s category."
          >
            <IconButton
              size="small"
              className="filter-help-btn"
              aria-label="Categories help"
            >
              <InfoOutlinedIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Stack>

        <TextField
          select
          size="small"
          fullWidth
          value={toCategoryDisplay(filters?.category)}
          onChange={(e) => handleCategoryChange(e.target.value)}
          SelectProps={{
            MenuProps: {
              PaperProps: { style: { maxHeight: 48 * 4 } },
            },
          }}
        >
          {categoryOptions.map((cat) => (
            <MenuItem key={cat} value={cat}>
              {cat}
            </MenuItem>
          ))}
        </TextField>
      </div>

      <div className="filter-clear-row">
        <Button
          variant="contained"
          color="error"
          onClick={handleClearFilters}
          className="filter-clear-btn"
          title="Clear all filters"
        >
          <FontAwesomeIcon icon={faTrashCan} />
        </Button>
      </div>
    </div>
  );
};

export default FilterPanel;
